// ─────────────────────────────────────────
// ts/sectionRail.ts — Menu lateral de seções
// Trilho flutuante à direita com as seções da página; marca a seção
// visível e rola até a escolhida. Só existe onde o HTML tem
// [data-section-rail] (hoje, a página Grid).
// ─────────────────────────────────────────

import { getTranslation } from '../i18n/index';
import { scrollToSection } from './utils';

/**
 * Os rótulos saem do selo de cada seção (`.sec-tag`), já traduzido pelo
 * renderer — assim o menu sempre diz o mesmo que a página, sem textos
 * duplicados no JSON.
 */
function collectSections(): Array<{ id: string; label: string; el: HTMLElement }> {
  return Array.from(document.querySelectorAll<HTMLElement>('section[id]'))
    .map((el) => ({
      id: el.id,
      label: el.querySelector('.sec-tag [data-i18n]')?.textContent?.trim() ?? '',
      el,
    }))
    .filter((s) => s.label);
}

export function initSectionRail(): void {
  const rail = document.querySelector<HTMLElement>('[data-section-rail]');
  if (!rail) return;

  const sections = collectSections();
  if (sections.length < 2) return;

  rail.setAttribute('aria-label', getTranslation().nav.sectionsLabel);
  rail.innerHTML = `<ol class="section-rail-list">${sections
    .map(
      ({ id, label }) => `
      <li><a class="section-rail-link" href="#${id}" data-rail-target="${id}">
        <span class="section-rail-label">${label.replace(/</g, '&lt;')}</span>
        <span class="section-rail-mark" aria-hidden="true"></span>
      </a></li>`,
    )
    .join('')}</ol>`;

  const links = new Map(
    Array.from(rail.querySelectorAll<HTMLAnchorElement>('[data-rail-target]')).map((a) => [a.dataset.railTarget!, a]),
  );

  rail.addEventListener('click', (event) => {
    const link = (event.target as Element).closest<HTMLAnchorElement>('[data-rail-target]');
    if (!link) return;
    event.preventDefault();
    scrollToSection({ targetId: link.dataset.railTarget! });
    history.replaceState(null, '', `${location.pathname}${location.search}#${link.dataset.railTarget}`);
    link.blur();
  });

  // Seção ativa: a que cruza a faixa logo acima do meio da tela.
  const setActive = (id: string): void => {
    links.forEach((link, key) => {
      const on = key === id;
      link.classList.toggle('is-active', on);
      if (on) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  };
  const sectionObserver = new IntersectionObserver(
    (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive((e.target as HTMLElement).id); }),
    { rootMargin: '-40% 0px -55% 0px' },
  );
  sections.forEach(({ el }) => sectionObserver.observe(el));

  // Sobre o hero o menu some: ali a página ainda está se apresentando.
  const hero = document.querySelector('section.hero');
  if (hero) {
    new IntersectionObserver(
      ([entry]) => rail.classList.toggle('is-visible', !entry.isIntersecting),
      { threshold: 0, rootMargin: '0px 0px -60% 0px' },
    ).observe(hero);
  } else {
    rail.classList.add('is-visible');
  }
}
